import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { closeCircleAPI, connectionsAPI } from '../api/client';
import Layout from '../components/Layout';
import { UserPlus, Eye, EyeOff } from 'lucide-react';
import '../styles/close-circle.css';

function getInitials(name) {
  if (!name) return '?';
  const parts = name.trim().split(' ');
  return (parts.length > 1 ? parts[0][0] + parts[parts.length - 1][0] : name[0]).toUpperCase();
}

export default function CloseCircle() {
  const navigate = useNavigate();
  const currentUserId = localStorage.getItem('userId');
  const [members, setMembers] = useState([]);
  const [friends, setFriends] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => { loadAll(); }, []);

  const loadAll = async () => {
    try {
      const [ccRes, connRes] = await Promise.all([
        closeCircleAPI.getAll(),
        connectionsAPI.getAll(),
      ]);
      setMembers(ccRes.data || []);
      setFriends((connRes.data || []).filter(c => c.status === 'accepted'));
    } catch (err) {
      console.error('Failed to load Close Circle:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleAdd = async (friendId) => {
    try {
      await closeCircleAPI.invite(friendId);
      loadAll();
    } catch {}
  };

  const handleRemove = async (id) => {
    try {
      await closeCircleAPI.remove(id);
      loadAll();
    } catch {}
  };

  const handleToggleTitles = async (id, current) => {
    try {
      await closeCircleAPI.setTitles(id, !current);
      loadAll();
    } catch {}
  };

  const memberUserIds = new Set(members.map(m => m.trusted_user_id));

  const addableFriends = friends.filter(c => {
    const fid = c.user_id === currentUserId ? c.connected_user_id : c.user_id;
    return !memberUserIds.has(fid);
  });

  if (loading) return <Layout title="Close Circle"><div className="cc-loading">Loading...</div></Layout>;

  return (
    <Layout title="Close Circle">
      <div className="cc-page">
        <div className="cc-page-header">
          <div>
            <h1 className="cc-page-title">Close Circle</h1>
            <p className="cc-page-sub">People whose availability you can see on your calendar.</p>
          </div>
        </div>

        {/* My circle */}
        <div className="cc-card">
          <div className="cc-card-label">In your Close Circle</div>
          {members.length === 0 ? (
            <p className="cc-empty">No one yet. Add a friend below.</p>
          ) : members.map(m => (
            <div key={m.id} className="cc-row">
              <div className="cc-avatar">{getInitials(m.display_name)}</div>
              <div className="cc-row-info">
                <span className="cc-row-name">{m.display_name}</span>
                <button
                  className="cc-titles-toggle"
                  onClick={() => handleToggleTitles(m.id, !!m.show_event_titles)}
                >
                  {m.show_event_titles
                    ? <><Eye size={13} /> Sees event titles</>
                    : <><EyeOff size={13} /> Busy/free only</>}
                </button>
              </div>
              <button className="cc-btn-ghost" onClick={() => handleRemove(m.id)}>Remove</button>
            </div>
          ))}
        </div>

        {/* Add friends */}
        {friends.length === 0 ? (
          <div className="cc-card">
            <p className="cc-empty">
              Connect with friends first to build your Close Circle.{' '}
              <button className="cc-link" onClick={() => navigate('/find-friends')}>Find friends</button>
            </p>
          </div>
        ) : addableFriends.length === 0 ? (
          <div className="cc-card">
            <p className="cc-empty">All your friends are already in your Close Circle.</p>
          </div>
        ) : (
          <div className="cc-card">
            <div className="cc-card-label">Add a friend</div>
            {addableFriends.map(c => {
              const friendId = c.user_id === currentUserId ? c.connected_user_id : c.user_id;
              return (
                <div key={c.id} className="cc-row">
                  <div className="cc-avatar">{getInitials(c.display_name)}</div>
                  <span className="cc-row-name" style={{ flex: 1 }}>{c.display_name}</span>
                  <button className="cc-btn-add" onClick={() => handleAdd(friendId)}>
                    <UserPlus size={14} /> Add
                  </button>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </Layout>
  );
}
